import { auth } from '@invisoutlet/firebase';
import type { AlertDisplay } from '@invisoutlet/shared/components';
import { AlertBannerBar, LayoutShell } from '@invisoutlet/shared/components';
import { useAuthContext } from '@invisoutlet/shared/context';
import { useWebSocketStore } from '@invisoutlet/shared/hooks';
import { type ReactNode, useEffect } from 'react';
import { EnterpriseSidebar } from './enterprise-sidebar.js';

interface EnterpriseLayoutProps {
  children: ReactNode;
  alerts?: AlertDisplay[];
  title?: string;
  onAcknowledgeAlert?: (alertId: string) => void;
}

export function EnterpriseLayout({
  children,
  alerts = [],
  title,
  onAcknowledgeAlert,
}: EnterpriseLayoutProps) {
  const { user } = useAuthContext();
  const { connect, disconnect } = useWebSocketStore();
  const activeAlertCount = alerts.filter((a) => a.status === 'active').length;

  useEffect(() => {
    if (!user) return;
    const getToken = () => {
      const currentUser = auth.currentUser;
      if (!currentUser) return Promise.reject(new Error('Not authenticated'));
      return currentUser.getIdToken();
    };
    connect(getToken);
    return () => disconnect();
  }, [user, connect, disconnect]);

  const alertBanner = (
    <AlertBannerBar
      alerts={alerts}
      readOnly={user?.readOnly ?? false}
      onAcknowledge={onAcknowledgeAlert}
    />
  );

  const sidebar = <EnterpriseSidebar alertCount={activeAlertCount} />;

  const topBar = title ? (
    <div className="flex items-center">
      <h1 className="text-sm font-semibold text-text-primary">{title}</h1>
    </div>
  ) : undefined;

  return (
    <LayoutShell alertBanner={alertBanner} sidebar={sidebar} topBar={topBar}>
      {children}
    </LayoutShell>
  );
}
